import { isRegisteredEventParticipant } from 'models';
import { useMemo } from 'react';
import { Button, Col, DateTimeInfo, Dropdown, More2LineIcon, Row, Skeleton, Space } from 'ui';
import { EventContent, useGetEventAction, useGetEventTimeInfo, useNodeEventQuery } from 'features/events';
import { Description } from '../description';
import { Participants } from '../participants';
import { Repeats } from '../repeats';
import { Tag } from '../tag';
import { Title } from '../title';
import { Type } from '../type';
import { useExtractIdFromKey } from './hooks';

type P = {
  eventId: string;
  getPathToEventDetail: (id: string) => string;
  navigateToEventDetail: (id: string) => Promise<void>;
};

const NodeEvent = ({ eventId, getPathToEventDetail, navigateToEventDetail }: P) => {
  const extractedId = useExtractIdFromKey(eventId);
  const getActions = useGetEventAction({ navigateToEventDetail });
  const getTimeInfo = useGetEventTimeInfo();

  const { data: event, isLoading: isLoadingEvent } = useNodeEventQuery({
    eventId: extractedId,
  });

  const { actions, contextHolders } = useMemo(() => {
    if (!event) {
      return { actions: [], contextHolders: null };
    }
    return getActions({ event });
  }, [event, getActions]);

  const shouldShowParticipantsList = useMemo(() => event?.type === 'telehealth_visit', [event]);
  const shouldShowParticipantsCount = useMemo(() => event?.type !== 'telehealth_visit', [event]);

  return (
    <Space block orientation="vertical" size="middle">
      {isLoadingEvent || !event ? (
        <Skeleton active paragraph={{ rows: 5 }} />
      ) : (
        <>
          <Row justify="space-between">
            <Col>
              <Tag endTime={event.endTime} startTime={event.startTime} type={event.type} />
            </Col>
            {actions.length > 0 ? (
              <Col>
                <Dropdown menu={{ items: actions }} trigger={['click']}>
                  <Button icon={<More2LineIcon />} type="link-secondary" />
                </Dropdown>
              </Col>
            ) : null}
          </Row>
          <Title getPathToEventDetail={getPathToEventDetail} id={event.id} status={event.status} title={event.name} />
          <DateTimeInfo timeInfo={getTimeInfo({ event })} />
          <Repeats />
          <Type />
          <Participants
            eventType={event.type}
            numberOfParticipants={shouldShowParticipantsCount ? event.goingCount : undefined}
            participants={
              shouldShowParticipantsList
                ? event.participants.map((participant) =>
                    isRegisteredEventParticipant(participant)
                      ? {
                          ...participant,
                          id: participant.ref,
                        }
                      : participant,
                  )
                : undefined
            }
          />
          <Description htmlContent={event.html} />
          <EventContent event={event} />
          {contextHolders}
        </>
      )}
    </Space>
  );
};

export default NodeEvent;
